import React, { useState } from 'react';
import { Box, Typography, Container, Button, Grid, useMediaQuery, useTheme, IconButton, Drawer } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import NavBar from '~/components/NavBar';
import Footer from '~/components/Footer';
import SideBar from '~/components/SideBar';
import SponsorsClient from '~/single-page/api/SponsorsClient';
import CausesClient from '~/single-page/api/CausesClient';
import { useApiCall } from '~/single-page/hooks/useApiCall';
import { APP_ROUTES } from '~/single-page/constants/routes';
import { useRailsContext } from '~/single-page/contexts/RailsContext';



// Componente do Banner Principal
const HeroBanner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:480px)');
  const { isAuthenticated } = useRailsContext();
  
  // Breakpoints específicos para ajustar o tamanho da imagem
  const needsSmallerImage = useMediaQuery('(min-width:481px) and (max-width:584px), (min-width:600px) and (max-width:780px), (min-width:900px) and (max-width:1060px)');
  const isBreakpoint1 = useMediaQuery('(min-width:481px) and (max-width:584px)'); // width: 143%, minHeight: 337px
  const isBreakpoint2 = useMediaQuery('(min-width:600px) and (max-width:780px)'); // width: 180px, minHeight: 575px  
  const isBreakpoint3 = useMediaQuery('(min-width:900px) and (max-width:1060px)'); // width: 150%, minHeight: 650px
  
  // Função para determinar width, minHeight e margens baseado nos breakpoints
  const getImageDimensions = () => {
    if (isBreakpoint1) {
      return { 
        width: '141%', 
        minHeight: '337px',
        marginTop: '24px',
        marginBottom: '-25px'
      };
    }
    if (isBreakpoint2) {
      return { 
        width: '180%', 
        minHeight: '575px',
        marginTop: '-60px',
        marginBottom: '-22px'
      };
    }
    if (isBreakpoint3) {
      return { 
        width: '150%', 
        minHeight: '650px',
        marginTop: '-51px',
        marginBottom: '-35px'
      };
    }
    return { 
      width: '100%', 
      minHeight: { xs: '300px', sm: '400px', md: '550px' },
      marginTop: 0,
      marginBottom: 0
    };
  };

  const imageDimensions = getImageDimensions();
  
  if (isMobile) {
    return (
      <>
        {/* Banner Mobile com imagem menor */}
        <Box
          sx={{
            width: '100%',
            minHeight: '180px',
            marginTop: '16px',
            backgroundImage: 'url("/images/main-banner/home_sponsors_mobile.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          {/* Decoração curva azul - Mobile */}
          <Box
            component="img"
            src="/images/sponsors_companies/blue-curve-decorator.png"
            alt="Decoração curva azul"
            sx={{
              position: 'absolute',
              left: '-1px',
              top: '107%',
              transform: 'translateY(-50%)',
              width: '38px',
              height: '103px',
              zIndex: 10,
              opacity: 0.9,
              pointerEvents: 'none'
            }}
          />
        </Box>
        
        {/* Seção de texto e botão mobile */}
        <Box
          sx={{
            backgroundColor: '#f5f5f5',
            px: 3,
            py: 4,
            textAlign: 'center'
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: '0.7rem',
              color: '#2A2A2A',
              lineHeight: 1.6,
              mb: 3,
              textAlign: 'left',
              ml: { xs: '8px' }
            }}
          >
            Conectamos ONGs a empresas, voluntários e doadores que querem gerar impacto social real.
            Nossa plataforma facilita parcerias, promove projetos e fortalece quem já está transformando comunidades.
            <strong> Junte-se a essa rede e faça parte da mudança.</strong>
          </Typography>
          
          {!isAuthenticated && (
            <Button
              component="a"
              href={APP_ROUTES.REGISTER}
              variant="contained"
              size="large"
              sx={{
                backgroundColor: '#294BB6',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.8rem',
                px: 4,
                py: 0.75,
                borderRadius: 2,
                textTransform: 'none',
                width: '100%',
                maxWidth: '280px',
                '&:hover': {
                  backgroundColor: '#1e3a8a',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(41, 75, 182, 0.4)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Quero fazer parte!
            </Button>
          )}
        </Box>
      </>
    );
  }
  
  // Layout desktop/tablet
  return (
    <Box
      sx={{
        width: imageDimensions.width,
        minHeight: imageDimensions.minHeight,
        marginTop: imageDimensions.marginTop,
        marginBottom: imageDimensions.marginBottom,
        backgroundImage: 'url("/images/main-banner/home_sponsors.png")',
        backgroundSize: needsSmallerImage ? 'contain' : 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        mx: 'auto' // Centraliza horizontalmente quando width não é 100%
      }}
    >
      {/* Botão CTA sobreposto à imagem */}
      {!isAuthenticated && (
        <Button
          component="a"
          href={APP_ROUTES.REGISTER}
          variant="contained"
          size="large"
          sx={{
            backgroundColor: '#294BB6',
            color: 'white',
            fontWeight: 'bold',
            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 1, sm: 1.2, md: 1.5 },
            borderRadius: 2,
            zIndex: 10,
            position: 'absolute',
            bottom: { xs: '20px', sm: '30px', md: '50px' },
            left: { xs: '20px', sm: '50px', md: '170px' },
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#1e3a8a',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(41, 75, 182, 0.4)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          Quero fazer parte!
        </Button>
      )}
    </Box>
  );
};

// Componente de Estatísticas
const StatsSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:710px)');
  const isTablet = useMediaQuery('(min-width:711px) and (max-width:1200px)');
  const isDesktop = useMediaQuery('(min-width:1201px)');

  // Componente reutilizável para cada estatística
  const StatItem = ({ icon, mainText, subText, iconAlt }) => (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      alignItems: isMobile ? 'flex-start' : 'center',
      width: '100%'
    }}>
      {/* Container do ícone e texto principal */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: isMobile ? 'flex-start' : 'center',
        mb: isMobile ? 0.3 : 0.5, // Reduzido: era 1, agora 0.3 mobile / 0.5 desktop
        width: '100%'
      }}>
        <Box
          component="img"
          src={icon}
          alt={iconAlt}
          sx={{
            width: isMobile ? '2.5rem' : (isTablet ? '2rem' : '2.8rem'),
            height: isMobile ? '2.5rem' : (isTablet ? '2rem' : '2.8rem'),
            mr: isMobile ? 0.5 : (isTablet ? 0.2 : 0.9), // Reduzido: era 1.5/1/1.5, agora 1.2/0.8/1.2
            flexShrink: 0
          }}
        />
        <Typography
          variant="h3"
          sx={{
            color: 'white',
            fontWeight: 'bold',
            fontSize: isMobile ? '1.3rem' : (isTablet ? '1.2rem' : '2.2rem'),
            whiteSpace: 'nowrap'
          }}
        >
          {mainText}
        </Typography>
      </Box>
      
      {/* Texto descritivo - mais próximo e mais à esquerda */}
      <Typography
        variant="body1"
        sx={{
          color: 'white',
          opacity: 0.9,
          fontSize: isMobile ? '0.7rem' : (isTablet ? '0.7rem' : '1rem'),
          textAlign: 'left', // Sempre à esquerda agora
          whiteSpace: 'nowrap',
          // Posicionamento mais à esquerda em relação ao mainText
          pl: isMobile 
            ? '3rem'  // Reduzido: era 4rem, agora 3.7rem (2.5rem ícone + 1.2rem margin)
            : (isTablet 
              ? '2.8rem'  // Tablet: 2rem ícone + 0.8rem margin
              : '2rem'    // Desktop: 2.8rem ícone + 1.2rem margin
            ),
          mt: isMobile ? '-0.8rem' : '-0.6rem', // Mais próximo: era -0.5rem/0, agora -0.2rem/-0.3rem
          ml: 0
        }}
      >
        {subText}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        backgroundColor: '#009FE3',
        width: '100%',
        py: { xs: 3, md: 4 }
      }}
    >
      <Container maxWidth="lg">
        {isMobile ? (
          // Layout Mobile: Coluna vertical com alinhamento consistente
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 2.5, // Reduzido: era 3, agora 2.5
            px: 3
          }}>
            <StatItem 
              icon="/images/sponsors_companies/ong.png"
              iconAlt="Ícone ONG"
              mainText="+1.200 ONGs"
              subText="cadastradas no estado"
            />
            <StatItem 
              icon="/images/sponsors_companies/monetization_on.png"
              iconAlt="Ícone Empresas"
              mainText="+350 empresas"
              subText="parceiras ativas em ações sociais"
            />
            <StatItem 
              icon="/images/sponsors_companies/charity.png"
              iconAlt="Ícone Pessoas"
              mainText="+1 milhão de pessoas"
              subText="impactadas pelos projetos apoiados"
            />
          </Box>
        ) : (
          // Layout Desktop/Tablet: Linha horizontal
          <Grid container spacing={isTablet ? 1.5 : 3} justifyContent="center" alignItems="flex-start">
            <Grid item xs={12} sm={4} md={4}>
              <StatItem 
                icon="/images/sponsors_companies/ong.png"
                iconAlt="Ícone ONG"
                mainText="+1.200 ONGs"
                subText="cadastradas no estado"
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <StatItem 
                icon="/images/sponsors_companies/monetization_on.png"
                iconAlt="Ícone Empresas"
                mainText="+350 empresas"
                subText="parceiras ativas em ações sociais"
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <StatItem 
                icon="/images/sponsors_companies/charity.png"
                iconAlt="Ícone Pessoas"
                mainText="+1 milhão de pessoas"
                subText="impactadas pelos projetos apoiados"
              />
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
};

const AboutSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:1018px)');
  
  if (isMobile) {
    return (
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          width: '100%',
          pt: 4,
        }}
      >
        <Container maxWidth="lg">
          {/* Texto primeiro em mobile */}
          <Box sx={{ px: { xs: 2, md: 4 }, mb: { xs: 4, md: 6 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' },
                color: '#000000',
                mb: { xs: 2, md: 3 },
                lineHeight: 1.3,
                textAlign: 'center'
              }}
            >
              Conectar, fortalecer, transformar:{' '}
              Descubra
              <br />
              como fazemos{' '}
              <Box
                component="span"
                sx={{
                  color: '#009FE3',
                  position: 'relative',
                  display: 'inline-block'
                }}
              >
                isso juntos!
                <Box
                  component="img"
                  src="/images/sponsors_companies/blue-underline.png"
                  alt="Underline decorativo"
                  sx={{
                    position: 'absolute',
                    bottom: '-6px',
                    left: '0',
                    width: '100%',
                    height: 'auto',
                    maxHeight: { xs: '8px', md: '12px' },
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
                color: '#000000',
                lineHeight: 1.6,
                mb: { xs: 2.5, md: 3 },
                textAlign: 'justify',
                maxWidth: { md: '80%' },
                mx: 'auto'
              }}
            >
              Atuar com o <strong>Bora Impactar</strong> é unir forças com uma rede 
              comprometida em gerar impacto social real. Facilitamos conexões 
              entre empresas e ONGs sérias, com projetos transparentes, 
              mensuráveis e alinhados aos Objetivos de Desenvolvimento 
              Sustentável (ODS).
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' },
                color: '#000000',
                fontWeight: 'bold',
                lineHeight: 1.6,
                textAlign: 'justify',
                maxWidth: { md: '80%' },
                mx: 'auto'
              }}
            >
              Oferecemos curadoria, visibilidade e suporte para que sua marca 
              seja reconhecida não só pelo que faz, mas pelo que transforma.
            </Typography>
          </Box>

          {/* Imagens depois em mobile - layout responsivo */}
          <Box 
            sx={{ 
              display: 'flex', 
              gap: { xs: 1.5, sm: 2, md: 2.5 },
              px: { xs: 2, md: 4 },
              justifyContent: 'center',
              alignItems: 'flex-start'
            }}
          >
            {/* Coluna esquerda - Duas imagens empilhadas */}
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: { xs: 1, md: 1.5 }
              }}
            >
              <Box
                component="img"
                src="/images/sponsors_companies/sponsor_companie1.png"
                alt="Primeira imagem empilhada"
                sx={{
                  width: { xs: '120px', sm: '140px', md: '160px' },
                  height: { xs: '109px', sm: '127px', md: '145px' },
                  borderRadius: { xs: '17px', md: '20px' },
                  objectFit: 'cover',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Box
                component="img"
                src="/images/sponsors_companies/sponsor_companie2.png"
                alt="Segunda imagem empilhada"
                sx={{
                  width: { xs: '120px', sm: '140px', md: '160px' },
                  height: { xs: '87px', sm: '101px', md: '116px' },
                  borderRadius: { xs: '17px', md: '20px' },
                  objectFit: 'cover',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                }}
              />
            </Box>
            
            {/* Coluna direita - Imagem maior */}
            <Box>
              <Box
                component="img"
                src="/images/sponsors_companies/sponsor_companie3.png"
                alt="Imagem lateral maior"
                sx={{
                  width: { xs: '120px', sm: '140px', md: '160px' },
                  height: { xs: '207px', sm: '241px', md: '276px' },
                  borderRadius: { xs: '17px', md: '20px' },
                  objectFit: 'cover',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }
  
  // Layout desktop/tablet (mantém o original)
  return (
    <Box
      sx={{
        backgroundColor: '#f5f5f5',
        width: '100%',
        py: { xs: 4, sm: 6, md: 8 }
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 3, sm: 4, md: 6 }} alignItems="center">
          {/* Coluna das Imagens (Esquerda) */}
          <Grid item xs={12} md={6}>
            <Box 
              sx={{ 
                display: 'flex', 
                gap: { xs: 1, sm: 1.5, md: 2 },
                maxWidth: { xs: '100%', sm: '400px', md: '450px' },
                mx: 'auto',
                height: { xs: 'auto', md: '330px' },
                flexDirection: { xs: 'column', sm: 'row', md: 'row' },
                alignItems: { xs: 'center', sm: 'flex-start' }
              }}
            >
              {/* Coluna esquerda - Duas imagens empilhadas */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'row', sm: 'column', md: 'column' }, 
                  gap: { xs: 1, sm: 2 },
                  justifyContent: { xs: 'center', sm: 'flex-start' }
                }}
              >
                <Box
                  component="img"
                  src="/images/sponsors_companies/sponsor_companie1.png"
                  alt="Primeira imagem empilhada"
                  sx={{
                    width: { xs: '120px', sm: '180px', md: '254px' },
                    height: { xs: '110px', sm: '166px', md: '235px' },
                    marginBottom: { xs: 0, sm: '13px', md: '13px' },
                    borderRadius: { xs: '8px', sm: '12px', md: '36px' },
                    objectFit: 'cover',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Box
                  component="img"
                  src="/images/sponsors_companies/sponsor_companie2.png"
                  alt="Segunda imagem empilhada"
                  sx={{
                    width: { xs: '120px', sm: '180px', md: '254px' },
                    height: { xs: '85px', sm: '125px', md: '177px' },
                    borderRadius: { xs: '8px', sm: '12px', md: '36px' },
                    objectFit: 'cover',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </Box>
              
              {/* Coluna direita - Imagem maior */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                mt: { xs: 2, sm: 0 }
              }}>
                <Box
                  component="img"
                  src="/images/sponsors_companies/sponsor_companie3.png"
                  alt="Imagem lateral maior"
                  sx={{
                    width: { xs: '180px', sm: '180px', md: '254px' },
                    height: { xs: '260px', sm: '312px', md: '442px' },
                    borderRadius: { xs: '8px', sm: '12px', md: '36px' },
                    objectFit: 'cover',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Coluna do Texto (Direita) */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              pl: { md: 3 },  
              mt: { xs: 4, sm: 6, md: 8 },
              px: { xs: 2, sm: 0 }
            }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.9rem' },
                  color: '#000000',
                  mb: { xs: 2, sm: 2.5, md: 3 },
                  lineHeight: 1.3,
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                Conectar, fortalecer, transformar:{' '}
                <br />
                Descubra como fazemos{' '}
                <Box
                  component="span"
                  sx={{
                    color: '#009FE3',
                    position: 'relative',
                    display: 'inline-block'
                  }}
                >
                  isso juntos!
                  {/* Imagem underline personalizada */}
                  <Box
                    component="img"
                    src="/images/sponsors_companies/blue-underline.png"
                    alt="Underline decorativo"
                    sx={{
                      position: 'absolute',
                      bottom: '-6px',
                      left: '0',
                      width: '100%',
                      height: 'auto',
                      maxHeight: '10px',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '0.88rem', sm: '0.95rem', md: '1.03rem' },
                  color: '#000000',
                  lineHeight: 1.6,
                  mb: { xs: 2, sm: 2.5, md: 3 },
                  textAlign: { xs: 'justify', md: 'left' }
                }}
              >
                Atuar com o <strong>Bora Impactar</strong> é unir forças com uma rede 
                comprometida em gerar impacto social real. Facilitamos conexões 
                entre empresas e ONGs sérias, com projetos transparentes, 
                mensuráveis e alinhados aos Objetivos de Desenvolvimento 
                Sustentável (ODS).
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '0.88rem', sm: '0.95rem', md: '1.03rem' },
                  color: '#000000',
                  fontWeight: 'bold',
                  lineHeight: 1.6,
                  textAlign: { xs: 'justify', md: 'left' }
                }}
              >
                Oferecemos curadoria, visibilidade e suporte para que sua marca 
                seja reconhecida não só pelo que faz, mas pelo que transforma.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

// Componente de Empresas Parceiras
const CompaniesSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:480px)');
  const isTablet = useMediaQuery('(max-width:1018px)') && !isMobile;
  const isDesktop = !isMobile && !isTablet;

  // Calcular quantos itens mostrar baseado no breakpoint
  const getItemsCount = () => {
    if (isMobile) return 9;  // 3 itens × 3 linhas = 9
    if (isTablet) return 12; // 4 itens × 3 linhas = 12
    return 15;               // 5 itens × 3 linhas = 15
  };

  const {
    data: sponsors = [],
    pending: loading,
    success,
  } = useApiCall({
    apiCall: SponsorsClient.sponsors,
    init: true,
  });

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          width: '100%',
          py: { xs: 6, sm: 8, md: 10 },
          textAlign: 'center'
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Carregando empresas parceiras...
        </Typography>
      </Box>
    );
  }
  
  if (!sponsors || sponsors.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: '#f5f5f5',
        width: '100%',
        py: { sm: 8, md: 2 },
        pt: { xs: 6 },
        pb: { sm: 0.1 },
      }}
    >
      <Container maxWidth="lg">
        {/* Título da seção */}
        <Box sx={{ textAlign: 'center', mt: { lg: 10 }, mb: { xs: 6, sm: 7, md: 8 } }}>
          {isMobile ? (
            // Layout Mobile: "Nossa rede conta com mais" / "de 100 empresas parceiras"
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  color: '#000000',
                  mb: 0.5,
                  lineHeight: 1.2
                }}
              >
                Nossa rede conta com mais
              </Typography>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    color: '#000000',
                    mb: 0,
                    lineHeight: 1.2,
                    display: 'inline'
                  }}
                >
                  de{' '}
                </Typography>
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    color: '#294BB6',
                    mb: 1,
                    lineHeight: 1.2,
                    display: 'inline'
                  }}
                >
                  100 empresas parceiras
                </Typography>
                {/* Sublinhado PNG */}
                <Box
                  component="img"
                  src="/images/sponsors_companies/blue-underline2.png"
                  alt="Sublinhado decorativo"
                  sx={{
                    position: 'absolute',
                    bottom: '-6px',
                    left: '55%',
                    transform: 'translateX(-50%)',
                    width: '90%',
                    height: 'auto',
                    maxHeight: '5.5px',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </Box>
          ) : isTablet ? (
            // Layout Tablet: Uma linha adaptativa
            <Box>
              <Typography
                variant="h2"
                component="span"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  color: '#000000',
                  lineHeight: 1.2,
                  mr: 1
                }}
              >
                Nossa rede conta com mais de
              </Typography>
              <Box component="span" sx={{ position: 'relative', display: 'inline-block' }}>
                <Typography
                  variant="h1"
                  component="span"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    color: '#294BB6',
                    lineHeight: 1.2
                  }}
                >
                  100 empresas parceiras
                </Typography>
                {/* Sublinhado PNG */}
                <Box
                  component="img"
                  src="/images/sponsors_companies/blue-underline2.png"
                  alt="Sublinhado decorativo"
                  sx={{
                    position: 'absolute',
                    bottom: '-9px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '95%',
                    height: 'auto',
                    maxHeight: '9px',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </Box>
          ) : (
            // Layout Desktop: "Nossa rede conta com mais de" / "100 empresas parceiras"
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '2.2rem',
                  color: '#000000',
                  mb: 0.5,
                  lineHeight: 1.2
                }}
              >
                Nossa rede conta com mais de
              </Typography>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '2.2rem',
                    color: '#294BB6',
                    mb: 1,
                    lineHeight: 1.2
                  }}
                >
                  100 empresas parceiras
                </Typography>
                {/* Sublinhado PNG */}
                <Box
                  component="img"
                  src="/images/sponsors_companies/blue-underline2.png"
                  alt="Sublinhado decorativo"
                  sx={{
                    position: 'absolute',
                    bottom: '-3px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '95%',
                    height: 'auto',
                    maxHeight: '10px',
                    objectFit: 'contain'
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>

        {/* Grid responsivo com quantidades diferentes */}
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} justifyContent="center">
          {sponsors.slice(0, getItemsCount()).map((sponsor, index) => (
            <Grid 
              item 
              xs={4}   // Mobile: 3 por linha (12/4 = 3)
              sm={3}   // Tablet: 4 por linha (12/3 = 4)  
              md={2.4} // Desktop: 5 por linha (12/2.4 = 5)
              key={sponsor.id || index}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {/* Imagem sem moldura */}
              {sponsor.photo_url ? (
                <Box
                  component="img"
                  src={sponsor.photo_url}
                  alt={sponsor.name}
                  sx={{
                    width: { xs: '80px', sm: '100px', md: '120px' },
                    height: { xs: '60px', sm: '75px', md: '90px' },
                    objectFit: 'contain',
                    filter: 'grayscale(100%)',
                    transition: 'filter 0.3s ease',
                    '&:hover': {
                      filter: 'grayscale(0%)'
                    }
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: { xs: '80px', sm: '100px', md: '120px' },
                    height: { xs: '60px', sm: '75px', md: '90px' },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f0f0f0',
                    borderRadius: 1
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: '0.6rem', sm: '0.7rem', md: '0.8rem' },
                      fontWeight: 'bold',
                      color: '#666',
                      textAlign: 'center',
                      lineHeight: 1.2
                    }}
                  >
                    {sponsor.name}
                  </Typography>
                </Box>
              )}
            </Grid>
          ))}
        </Grid>

        {/* Botão CTA com redirecionamento */}
        <Box sx={{ textAlign: 'center', mt: { xs: 6, sm: 7, md: 8 } }}>
          <Button
            component={Link}
            to="/sponsors"
            variant="contained"
            size="large"
            sx={{
              backgroundColor: '#294BB6',
              color: 'white',
              fontWeight: 'bold',
              fontSize: { xs: '0.8rem', sm: '1rem', md: '1.1rem' }, // Ajustado para mobile
              px: { xs: 4, sm: 4, md: 5 }, // Ajustado para mobile
              py: { xs: 0.75, sm: 1.2, md: 1.5 }, // Ajustado para mobile
              borderRadius: 2,
              textTransform: 'none',
              textDecoration: 'none',
              width: { xs: '100%', sm: 'auto' }, // 100% apenas no mobile
              maxWidth: { xs: '280px', sm: 'none' }, // 280px apenas no mobile
              '&:hover': {
                backgroundColor: '#1e3a8a',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(41, 75, 182, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Quero saber mais
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

// Componente da Curva Decorativa Laranja
const DecorativeCurveSection = () => (
  <Box
    sx={{
      width: '100%',
      height: { xs: '80px', sm: '100px', md: '120px' }, // Altura mínima para a seção
      position: 'relative',
      backgroundColor: 'transparent' // Transparente para não interferir
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        top: { xs: '25%', sm: '50%', md: '70%' }, // Mobile: 138%, outros: 50%
        transform: { xs: 'none', sm: 'translateY(-50%)', md: 'translateY(-50%)' }, // Mobile: sem transform, outros: centralizado
        width: { xs: '40px', sm: '60px', md: '80px' },
        height: { xs: '200px', sm: '300px', md: '400px' },
        marginLeft: { xs: '-16px', sm: '0px', md: '-8px' }, // Mobile: -16px, Desktop: -8px
        backgroundImage: 'url("/images/sponsors_companies/orange-curve-decorator.png")',
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        zIndex: 10,
        pointerEvents: 'none' // Para não interferir com cliques
      }}
    />
  </Box>
);

// Componente de Depoimentos
const TestimonialsSection = () => {
  const theme = useTheme();

  const isMobile = useMediaQuery('(max-width:599px)');  // 0-599px
  const isTablet = useMediaQuery('(min-width:600px) and (max-width:768px)');  // 600-768px  
  const isDesktop = useMediaQuery('(min-width:769px)'); // 769px+
  
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Array de depoimentos
  const testimonials = [
    {
      id: 1,
      name: "Renato Carneiro",
      position: "Presidente do Instituto\nSocial Plus",
      photo: "/images/sponsors_companies/testimonials/testimonial1.jpeg",
      testimonial: "Nós, que fazemos o Instituto Social Plus, acreditamos que grandes transformações nascem quando organizações caminham juntas. A experiência com o Bora Impactar tem sido inspiradora, porque nos sentimos apoiados, valorizados e desafiados a crescer ainda mais. A plataforma nos conecta, nos fortalece e nos mostra que não estamos sozinhos na missão de gerar impacto social. Somos profundamente gratos por essa parceria que potencializa nossa voz e amplia nosso alcance. Seguimos juntos, impactando vidas e construindo futuros melhores!"
    },
    {
      id: 2,
      name: "Flora Pessôa",
      position: "Presidente da Casa Da\nCriança Marcelo Asfora",
      photo: "/images/sponsors_companies/testimonials/testimonial3.jpeg",
      testimonial: "O Bora Impactar é uma oportunidade de ganhar visibilidade e fazer novas parcerias. Equipe jovem e comprometida em gerar impacto social."
    },
    {
      id: 3,
      name: "João Victor Leite", 
      position: "Coordenador de projeto de\nOratória da Divina Providência",
      photo: "/images/sponsors_companies/testimonials/testimonial2.jpeg",
      testimonial: "Bora impactar, chega em um momento que a evolução da tecnologia no tocante a comunicação avança. O terceiro setor como parte integrante na construção de uma sociedade mais justa só tem a ganhar. Vejo que é necessário que as organizações envolvidas possam cada vez mais compartilhar as vivências e o poder público não só apoiar uma plataforma, mas efetivar financiamentos e aportes a organizações que muita vezes fazem o papel das políticas públicas."
    }
  ];

  const nextSlide = () => {
    if (isDesktop) {
      // Desktop: navega por pares (2 depoimentos por slide)
      const maxSlides = Math.ceil(testimonials.length / 2);
      setCurrentSlide((prev) => (prev + 1) % maxSlides);
    } else {
      // Mobile/Tablet: navega individualmente
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }
  };

  const prevSlide = () => {
    if (isDesktop) {
      // Desktop: navega por pares (2 depoimentos por slide)
      const maxSlides = Math.ceil(testimonials.length / 2);
      setCurrentSlide((prev) => (prev - 1 + maxSlides) % maxSlides);
    } else {
      // Mobile/Tablet: navega individualmente
      setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    }
  };

  // Funções para controle de touch/swipe
  const handleTouchStart = (e) => {
    setTouchEnd(0);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Componente do Card de Depoimento
  const TestimonialCard = ({ testimonial }) => (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: { xs: '20px', sm: '20px' },
        p: { 
          xs: 1.5,    // Mobile (0-599px)
          sm: 2.5,    // Tablet (600-768px)
          md: 3       // Desktop (769px+)
        },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        minHeight: { 
          xs: '150px',    // Mobile
          sm: '200px',    // Tablet
          md: '250px'     // Desktop
        },
        width: { 
          xs: '250px',    // Mobile
          sm: '340px',    // Tablet 
          md: '100%'      // Desktop (usa o grid)
        },
        mx: { xs: 0.5, sm: 0 }
      }}
    >
      {/* Avatar e Info */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: { xs: 1.5, sm: 2, md: 3 }
      }}>
        {testimonial.photo ? (
          <Box
            component="img"
            src={testimonial.photo}
            alt={`Foto de ${testimonial.name}`}
            sx={{
              width: { 
                xs: '50px',   // Mobile
                sm: '70px',   // Tablet
                md: '90px'    // Desktop
              },
              height: { 
                xs: '50px',
                sm: '70px',   // Tablet
                md: '90px'
              },
              borderRadius: '50%',
              mr: { xs: 1.2, sm: 1.5, md: 2 },
              flexShrink: 0,
              objectFit: 'cover',
              border: '2px solid #E0E0E0'
            }}
          />
        ) : (
          <Box
            sx={{
              width: { 
                xs: '50px',   // Mobile
                sm: '70px',   // Tablet
                md: '90px'    // Desktop
              },
              height: { 
                xs: '50px',
                sm: '70px',   // Tablet
                md: '90px'
              },
              borderRadius: '50%',
              backgroundColor: '#E0E0E0',
              mr: { xs: 1.2, sm: 1.5, md: 2 },
              flexShrink: 0
            }}
          />
        )}
        
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              fontSize: { 
                xs: '0.85rem',  // Mobile
                sm: '1rem',     // Tablet
                md: '1.1rem'    // Desktop
              },
              color: '#000',
              mb: 0.3
            }}
          >
            {testimonial.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: { 
                xs: '0.65rem',  // Mobile
                sm: '0.8rem',   // Tablet
                md: '0.9rem'    // Desktop
              },
              color: '#666',
              lineHeight: 1.2,
              whiteSpace: 'pre-line'
            }}
          >
            {testimonial.position}
          </Typography>
        </Box>
      </Box>

      {/* Depoimento */}
      <Typography
        variant="body1"
        sx={{
          fontSize: { 
            xs: '0.7rem',   // Mobile
            sm: '0.85rem',  // Tablet
            md: '0.95rem'   // Desktop
          },
          color: '#333',
          lineHeight: 1.4,
          fontStyle: 'italic',
          flex: 1
        }}
      >
        "{testimonial.testimonial}"
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        backgroundColor: '#009FE3',
        width: '100%',
        py: { xs: 6, sm: 8, md: 8 }
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="flex-start">
          {/* Coluna da esquerda - Título */}
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              pr: { md: 3 },
              mb: { xs: 4, md: 0 },
              textAlign: { xs: 'center', md: 'left' }
            }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.2rem' },
                    color: 'white',
                    mb: { xs: 2, md: 3 },
                    lineHeight: 1.2
                  }}
                >
                  {isMobile ? (
                    // Mobile: quebra na segunda linha
                    <>
                      O que nossos
                      <br />
                      parceiros têm{' '}
                      <Box
                        component="span"
                        sx={{
                          position: 'relative',
                          display: 'inline-block',
                          color: '#0B236D'
                        }}
                      >
                        a dizer?
                        <Box
                          component="img"
                          src="/images/sponsors_companies/blue-underline3.png"
                          alt="Underline decorativo"
                          sx={{
                            position: 'absolute',
                            bottom: '-4px',
                            left: '0',
                            width: '100%',
                            height: 'auto',
                            maxHeight: '6px',
                            objectFit: 'contain'
                          }}
                        />
                      </Box>
                    </>
                  ) : (
                    // Tablet e Desktop: uma linha só
                    <>
                      O que nossos parceiros têm{' '}
                      <Box
                        component="span"
                        sx={{
                          position: 'relative',
                          display: 'inline-block',
                          color: '#0B236D'
                        }}
                      >
                        a dizer?
                        <Box
                          component="img"
                          src="/images/sponsors_companies/blue-underline3.png"
                          alt="Underline decorativo"
                          sx={{
                            position: 'absolute',
                            bottom: '-6px',
                            left: '0',
                            width: '100%',
                            height: 'auto',
                            maxHeight: '8px',
                            objectFit: 'contain'
                          }}
                        />
                      </Box>
                    </>
                  )}
                </Typography>
              </Box>
              
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  color: 'white',
                  opacity: 0.9,
                  lineHeight: 1.6,
                  maxWidth: { xs: '100%', md: '280px' },
                  textAlign: 'left',
                  marginLeft: { xs: '10px', md: '0', lg: '0' }
                }}
              >
                Reunimos depoimentos de clientes e parceiros que já vivenciaram nossos serviços, para que você conheça, na prática, a experiência e os resultados que entregamos.
              </Typography>
            </Box>
          </Grid>

          {/* Coluna da direita - Depoimentos - LÓGICA CORRIGIDA */}
          <Grid item xs={12} md={8}>
            {isMobile ? (
              // Layout Mobile: Carrossel pequeno (0-599px)
              <Box sx={{ 
                position: 'relative', 
                overflow: 'hidden',
                width: '100%',
                px: 2
              }}>
                <Box
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  sx={{
                    display: 'flex',
                    transition: 'transform 0.3s ease-in-out',
                    transform: `translateX(-${currentSlide * 270}px)`, // Mobile: 270px
                    gap: 1,
                    touchAction: 'pan-y pinch-zoom',
                    userSelect: 'none',
                  }}
                >
                  {testimonials.map((testimonial) => (
                    <Box key={testimonial.id} sx={{ flexShrink: 0 }}>
                      <TestimonialCard testimonial={testimonial} />
                    </Box>
                  ))}
                </Box>

                {/* Controles de navegação Mobile */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 2.5,
                  gap: 1,
                  alignItems: 'center'
                }}>
                  {/* Seta esquerda */}
                  <IconButton
                    onClick={prevSlide}
                    disabled={testimonials.length <= 1}
                    sx={{
                      color: 'white',
                      padding: 0,
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'transparent'
                      },
                      '&:disabled': {
                        opacity: 0.3
                      }
                    }}
                  >
                    <ArrowBackIosIcon sx={{ fontSize: '16px' }} />
                  </IconButton>

                  {/* Traço esquerdo */}
                  <Box sx={{
                    width: '12px',
                    height: '2px',
                    backgroundColor: 'white',
                    mr: 0.5,
                    ml: -2.7
                  }} />

                  {/* Indicadores de pontos */}
                  {testimonials.map((_, index) => (
                    <Box
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: currentSlide === index ? '#0B236D' : 'rgba(255, 255, 255, 0.5)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        mx: 0.5,
                        '&:hover': {
                          transform: 'scale(1.2)'
                        }
                      }}
                    />
                  ))}

                  {/* Traço direito */}
                  <Box
                    sx={{
                      width: '12px',
                      height: '2px',
                      backgroundColor: 'white',
                      ml: 0.5,
                      mr: -2.2
                    }}
                  />

                  {/* Seta direita */}
                  <IconButton
                    onClick={nextSlide}
                    disabled={testimonials.length <= 1}
                    sx={{
                      color: 'white',
                      padding: 0,
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'transparent'
                      },
                      '&:disabled': {
                        opacity: 0.3
                      }
                    }}
                  >
                    <ArrowForwardIosIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                </Box>
              </Box>
            ) : isTablet ? (
              // Layout Tablet: Carrossel maior (600-768px)
              <Box sx={{ 
                position: 'relative', 
                overflow: 'hidden',
                width: '100%',
                px: 1
              }}>
                <Box
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  sx={{
                    display: 'flex',
                    transition: 'transform 0.3s ease-in-out',
                    transform: `translateX(-${currentSlide * 360}px)`, // Tablet: 360px
                    gap: 2,
                    touchAction: 'pan-y pinch-zoom',
                    userSelect: 'none',
                  }}
                >
                  {testimonials.map((testimonial) => (
                    <Box key={testimonial.id} sx={{ flexShrink: 0 }}>
                      <TestimonialCard testimonial={testimonial} />
                    </Box>
                  ))}
                </Box>

                {/* Controles de navegação Tablet */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 3,
                  gap: 1,
                  alignItems: 'center'
                }}>
                  {/* Seta esquerda */}
                  <IconButton
                    onClick={prevSlide}
                    disabled={testimonials.length <= 1}
                    sx={{
                      color: 'white',
                      padding: 0,
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'transparent'
                      },
                      '&:disabled': {
                        opacity: 0.3
                      }
                    }}
                  >
                    <ArrowBackIosIcon sx={{ fontSize: '18px' }} />
                  </IconButton>

                  {/* Traço esquerdo */}
                  <Box sx={{
                    width: '14px',
                    height: '2px',
                    backgroundColor: 'white',
                    mr: 0.8,
                    ml: -3
                  }} />

                  {/* Indicadores de pontos */}
                  {testimonials.map((_, index) => (
                    <Box
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      sx={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: currentSlide === index ? '#0B236D' : 'rgba(255, 255, 255, 0.5)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        mx: 0.5,
                        '&:hover': {
                          transform: 'scale(1.2)'
                        }
                      }}
                    />
                  ))}

                  {/* Traço direito */}
                  <Box
                    sx={{
                      width: '14px',
                      height: '2px',
                      backgroundColor: 'white',
                      ml: 0.8,
                      mr: -2.5
                    }}
                  />

                  {/* Seta direita */}
                  <IconButton
                    onClick={nextSlide}
                    disabled={testimonials.length <= 1}
                    sx={{
                      color: 'white',
                      padding: 0,
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'transparent'
                      },
                      '&:disabled': {
                        opacity: 0.3
                      }
                    }}
                  >
                    <ArrowForwardIosIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                </Box>
              </Box>
            ) : (
              // Layout Desktop: Carrossel mostrando 2 por vez (769px+)
              <Box sx={{ 
                position: 'relative', 
                overflow: 'hidden',
                width: '100%'
              }}>
                <Box
                  sx={{
                    display: 'flex',
                    transition: 'transform 0.3s ease-in-out',
                    transform: `translateX(-${currentSlide * 100}%)`,
                    gap: 4
                  }}
                >
                  {/* Agrupar depoimentos em pares para desktop */}
                  {Array.from({ length: Math.ceil(testimonials.length / 2) }, (_, slideIndex) => (
                    <Box 
                      key={slideIndex}
                      sx={{ 
                        minWidth: '100%',
                        display: 'flex',
                        gap: 4
                      }}
                    >
                      {testimonials.slice(slideIndex * 2, slideIndex * 2 + 2).map((testimonial) => (
                        <Box key={testimonial.id} sx={{ flex: 1 }}>
                          <TestimonialCard testimonial={testimonial} />
                        </Box>
                      ))}
                      {/* Se for ímpar, adiciona espaço vazio */}
                      {testimonials.slice(slideIndex * 2, slideIndex * 2 + 2).length === 1 && (
                        <Box sx={{ flex: 1 }} />
                      )}
                    </Box>
                  ))}
                </Box>

                {/* Controles de navegação Desktop */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 4,
                  gap: 1,
                  alignItems: 'center'
                }}>
                  {/* Seta esquerda */}
                  <IconButton
                    onClick={prevSlide}
                    disabled={Math.ceil(testimonials.length / 2) <= 1}
                    sx={{
                      color: 'white',
                      padding: 0,
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'transparent'
                      },
                      '&:disabled': {
                        opacity: 0.3
                      }
                    }}
                  >
                    <ArrowBackIosIcon sx={{ fontSize: '20px' }} />
                  </IconButton>

                  {/* Traço esquerdo */}
                  <Box sx={{
                    width: '16px',
                    height: '2px',
                    backgroundColor: 'white',
                    mr: 1,
                    ml: -3.3
                  }} />

                  {/* Indicadores de pontos */}
                  {Array.from({ length: Math.ceil(testimonials.length / 2) }, (_, index) => (
                    <Box
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      sx={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: currentSlide === index ? '#0B236D' : 'rgba(255, 255, 255, 0.5)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        mx: 0.5,
                        '&:hover': {
                          transform: 'scale(1.2)'
                        }
                      }}
                    />
                  ))}

                  {/* Traço direito */}
                  <Box
                    sx={{
                      width: '16px',
                      height: '2px',
                      backgroundColor: 'white',
                      ml: 1,
                      mr: -2.6
                    }}
                  />

                  {/* Seta direita */}
                  <IconButton
                    onClick={nextSlide}
                    disabled={Math.ceil(testimonials.length / 2) <= 1}
                    sx={{
                      color: 'white',
                      padding: 0,
                      minWidth: 'auto',
                      '&:hover': {
                        backgroundColor: 'transparent'
                      },
                      '&:disabled': {
                        opacity: 0.3
                      }
                    }}
                  >
                    <ArrowForwardIosIcon sx={{ fontSize: '20px' }} />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Botão "Ver todas" - aparece em todas as resoluções */}
            {/* {(isMobile || isTablet) && (
              <Box sx={{ textAlign: 'center', mt: { xs: 3, sm: 4 } }}>
                <Typography
                  component="a" // ou "button" se preferir
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px', // Espaço entre texto e linha
                    textDecorationThickness: '1px', // Espessura da linha
                    cursor: 'pointer',
                    display: 'inline-block',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      opacity: 0.8,
                      textDecorationThickness: '3px', // Linha mais grossa no hover
                    },
                    '&:active': {
                      opacity: 0.6
                    }
                  }}
                  onClick={() => {
                    // Sua lógica de navegação aqui
                    console.log('Ver todas clicado');
                  }}
                >
                  Ver todas
                </Typography>
              </Box>
            )} */}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

// Componente de Causas com carrossel 4x4
const CausesSection = ({ navigate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:599px)');
  const isTablet = useMediaQuery('(min-width:600px) and (max-width:768px)');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Buscar causas do banco de dados
  const {
    data: causes = [],
    pending: loading,
    success,
  } = useApiCall({
    apiCall: CausesClient.index,
    init: true,
  });

  // Calcular quantos grupos de 4 itens disponível
  const totalSlides = Math.ceil(causes.length / 4);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  // Funções para controle de touch/swipe
  const handleTouchStart = (e) => {
    setTouchEnd(0);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    } else if (isRightSwipe) {
      prevSlide();
    }
  };

  // Componente do Card de Causa
  const CauseCard = ({ cause, navigate }) => (
    <Box
      onClick={() => navigate(APP_ROUTES.CAUSE_NGOS(cause.id))}
      sx={{
        backgroundColor: 'white',
        borderRadius: { xs: '12px', md: '16px' },
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        height: { 
          xs: '140px',  // Mobile menor para caber 2x2
          sm: '240px',  
          md: '280px' 
        },
        width: '100%',
        flexShrink: 0,
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 24px rgba(0, 0, 0, 0.15)'
        }
      }}
    >
      {/* Imagem */}
      <Box
        sx={{
          height: '80%',
          backgroundImage: `url(${cause.cover_image_url || '/images/default-cause.png'})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      
      {/* Título */}
      <Box
        sx={{
          height: '20%',
          backgroundColor: '#009FE3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: 'white',
            fontWeight: 'bold',
            fontSize: { 
              xs: '0.7rem',   // Mobile bem menor
              sm: '1.1rem', 
              md: '1.3rem' 
            },
            textAlign: 'center',
            lineHeight: 1.1
          }}
        >
          {cause.name}
        </Typography>
      </Box>
    </Box>
  );

  // Função para agrupar causas em grupos de 4
  const groupCauses = (causes, groupSize = 4) => {
    const groups = [];
    for (let i = 0; i < causes.length; i += groupSize) {
      groups.push(causes.slice(i, i + groupSize));
    }
    return groups;
  };

  const causesGroups = groupCauses(causes, 4);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: '#009FE3',
          width: '100%',
          py: { xs: 6, sm: 8, md: 10 },
          textAlign: 'center'
        }}
      >
        <Typography variant="h6" color="white">
          Carregando causas...
        </Typography>
      </Box>
    );
  }

  if (!causes || causes.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        // Fundo com duas cores - gradiente vertical
        background: `linear-gradient(180deg, #f5f5f5 0%, #f5f5f5 45%, #294BB5 45%, #294BB5 100%)`,
        py: { xs: 6, sm: 8, md: 10 }
      }}
    >
      <Container maxWidth="lg">
        {/* Título da seção */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 'bold',
              // fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
              fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.2rem' },
              color: '#000000',
              mb: 2
            }}
          >
            Conheça&nbsp; 
            <Box sx={{ display: { xs: 'block', sm: 'none' } }} component="br" />
            nossas{' '}
            <Box
              component="span"
              sx={{
                color: '#009FE3',
                position: 'relative',
                display: 'inline-block'
              }}
            >
              causas
              {/* Sublinhado decorativo */}
              <Box
                component="img"
                src="/images/sponsors_companies/blue-underline.png"
                alt="Underline decorativo"
                sx={{
                  position: 'absolute',
                  bottom: '-6px',
                  left: '-2px',
                  width: '108%',
                  height: 'auto',
                  maxHeight: '8px',
                  objectFit: 'contain'
                }}
              />
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#000000',
              opacity: 0.9,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
              maxWidth: '600px',
              textAlign: { xs: 'left', sm: 'center', md: 'left' }, // opcional: definir alinhamento
              ml: { xs: '25px', sm: 'auto', md: '318px' },
              mr: { xs: 0, sm: 'auto', md: 0 }
            }}
          >
            Onde unimos <strong>propósito e ação</strong> para transformar comunidades.
          </Typography>
        </Box>

        {/* Carrossel de Causas */}
        <Box sx={{ position: 'relative', mb: { xs: 6, sm: 8, md: 10 } }}>
          {/* Decoração curva laranja - Desktop/Tablet */}
          <Box
            component="img"
            src="/images/sponsors_companies/orange-curve-decorator2.png"
            alt="Decoração curva laranja"
            sx={{
              display: { xs: 'none', sm: 'block' }, // Esconde no mobile
              position: 'absolute',
              right: { sm: -40, md: -144 },
              top: { sm: '15%', md: '23%' },
              width: { sm: '180px', md: '260px' },
              height: { sm: '180px', md: '204px' },
              zIndex: 0,
              opacity: 0.9,
              pointerEvents: 'none'
            }}
          />

          {/* Decoração curva laranja - Mobile */}
          <Box
            component="img"
            src="/images/sponsors_companies/orange-curve-decorator3.png"
            alt="Decoração curva laranja mobile"
            sx={{
              display: { xs: 'block', sm: 'none' }, // Mostra apenas no mobile
              position: 'absolute',
              right: -20,
              top: '31%',
              width: '116px',
              height: '190px',
              zIndex: 0,
              opacity: 0.9,
              pointerEvents: 'none'
            }}
          />

          <Box sx={{ 
            position: 'relative', 
            overflow: 'hidden',
            width: '100%',
            zIndex: 1
          }}>
            <Box
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              sx={{
                display: 'flex',
                transition: 'transform 0.3s ease-in-out',
                transform: `translateX(-${currentSlide * 100}%)`,
                touchAction: 'pan-y pinch-zoom',
                userSelect: 'none',
              }}
            >
              {causesGroups.map((group, slideIndex) => (
                <Box 
                  key={slideIndex}
                  sx={{ 
                    minWidth: '100%',
                    px: { xs: 2, sm: 1 }
                  }}
                >
                  {isMobile ? (
                    // Mobile: Grid 2x2
                    <Grid container spacing={2}>
                      {group.map((cause, index) => (
                        <Grid item xs={6} key={cause.id || index}>
                          <CauseCard cause={cause} navigate={navigate} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    // Tablet/Desktop: 4 colunas horizontais
                    <Grid container spacing={{ sm: 3, md: 4 }}>
                      {group.map((cause, index) => (
                        <Grid item xs={12} sm={6} md={3} key={cause.id || index}>
                          <CauseCard cause={cause} navigate={navigate} />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Indicadores de navegação */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            mt: 4,
            gap: 1,
            alignItems: 'center',
            position: 'relative',
            zIndex: 2
          }}>
            {/* Seta esquerda */}
            <IconButton
              onClick={prevSlide}
              disabled={totalSlides <= 1}
              sx={{
                color: '#f5f5f5',
                padding: 0,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent'
                },
                '&:disabled': {
                  opacity: 0.3
                }
              }}
            >
              <ArrowBackIosIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} />
            </IconButton>

            {/* Traço esquerdo */}
            <Box sx={{
              width: { xs: '12px', md: '16px' },
              height: '2px',
              backgroundColor: '#f5f5f5',
              mr: { xs: 0.5, md: 1 },
              ml: { xs: -2.7, md: -3.3 }
            }} />

            {/* Indicadores de pontos */}
            {causesGroups.map((_, index) => (
              <Box
                key={index}
                onClick={() => setCurrentSlide(index)}
                sx={{
                  width: { xs: '8px', md: '12px' },
                  height: { xs: '8px', md: '12px' },
                  borderRadius: '50%',
                  backgroundColor: currentSlide === index ? '#009FE3' : '#f5f5f5',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  mx: 0.5,
                  '&:hover': {
                    transform: 'scale(1.2)'
                  }
                }}
              />
            ))}

            {/* Traço direito */}
            <Box
              sx={{
                width: { xs: '12px', md: '16px' },
                height: '2px',
                backgroundColor: '#f5f5f5',
                ml: { xs: 0.5, md: 1 },
                mr: { xs: -2.2, md: -2.6 }
              }}
            />

            {/* Seta direita */}
            <IconButton
              onClick={nextSlide}
              disabled={totalSlides <= 1}
              sx={{
                color: '#f5f5f5',
                padding: 0,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'transparent'
                },
                '&:disabled': {
                  opacity: 0.3
                }
              }}
            >
              <ArrowForwardIosIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} />
            </IconButton>
          </Box>
        </Box>

        {/* Seção "Descubra a Causa que Move Você" */}
        <Grid container spacing={{ xs: 4, sm: 6, md: 8 }} alignItems="center">
          {/* Texto à esquerda */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem' },
                  color: 'white',
                  mb: 3,
                  lineHeight: 1.2,
                  textAlign: { xs: 'center', sm: 'center', md: 'left' }
                }}
              >
                Descubra a Causa que 
                <br />
                Move Você{' '}
                <Box
                  component="span"
                  sx={{ fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2rem' } }}
                >
                  🌱
                </Box>
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  color: 'white',
                  opacity: 0.9,
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  lineHeight: 1.6,
                  maxWidth: { xs: '100%', md: '400px' },
                  ml: { xs: '11px', sm: '11px', md: 0 }
                }}
              >
                Que tal fazer um teste rápido e descobrir qual causa mais combina com o seu jeito de ver a vida?
              </Typography>
            </Box>
          </Grid>

          {/* Imagem/Link à direita */}
          {/* Imagem/Link à direita */}
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: { xs: 'center', sm: 'center', md: 'right' } }}>
              <Box
                component="a"
                href="https://descubrasuacausa.net.br/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-block',
                  textDecoration: 'none',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              >
                <Box
                  component="img"
                  src={isMobile ? "/images/sponsors_companies/discover-your-cause_mobile.png" : "/images/sponsors_companies/discover-your-cause.png"}
                  alt="Descubra sua causa"
                  sx={{
                    width: { 
                      xs: '280px',     // Mobile: mantém 280px
                      sm: '400px',     // Tablet: reduz para 280px
                      md: '500px'      // Desktop: reduz para 450px
                    },
                    height: 'auto',
                    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.2)',
                    ml: { 
                      xs: 0,           // Mobile: centralizado
                      sm: '-20px',     // Tablet: move para esquerda
                      md: '-80px'      // Desktop: move mais para esquerda
                    }
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

// Componente principal da página
export default function SponsorsCompaniesPage() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const isMobile = useMediaQuery('(max-width:700px)');
  
  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };
  
  // Reset de estilos globais para garantir que a página seja independente
  React.useEffect(() => {
    // Remove margem e padding do body
    const originalBodyStyle = document.body.style.cssText;
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.backgroundColor = '#f5f5f5';
    
    // Cleanup quando o componente é desmontado
    return () => {
      document.body.style.cssText = originalBodyStyle;
    };
  }, []);

  return (
    <Box 
      sx={{ 
        width: '100vw', 
        minHeight: '100vh',
        margin: 0, 
        padding: 0,
        backgroundColor: '#f5f5f5',
        position: 'relative',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <NavBar toggleDrawer={handleDrawerToggle} />
      
      {/* Drawer Mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerClose}
          onTransitionEnd={handleDrawerTransitionEnd}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: isMobile ? "block" : "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: "100%",
              px: 3,
              pt: 2,
              position: 'relative',
              backgroundColor: (theme) => theme.palette.background.default,
            },
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            mb: 2
          }}>
            <IconButton 
              onClick={handleDrawerClose}
              sx={{ color: 'text.primary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <SideBar drawerToggle={handleDrawerClose} />
        </Drawer>
      <Box sx={{ mt: { xs: '70px', sm: '80px', md: '88px' } }}>
        <HeroBanner />
        <StatsSection />
        <AboutSection />
        <CompaniesSection />
        <DecorativeCurveSection />
        <TestimonialsSection />
        <CausesSection navigate={navigate} />
      </Box>
      <Footer />
    </Box>
  );
}
